import { useState } from 'react';
import { motion } from 'framer-motion';

export default function HomePage() {
  const [language, setLanguage] = useState('en');

  const t = {
    en: {
      banner: "Because Where + How It’s Made Still Matters",
      sub: "Most brands can’t trace their products. We craft ours from the soil up.",
      tagline: "Ethical. Traceable. Designed in Canada. Made with family.",
      bullets: [
        "No middlemen. No mystery suppliers.",
        "Premium Egyptian cotton — no blends or tricks.",
        "Transparent pricing. Ethical production.",
        "Designed in Canada. Tailored for modern life."
      ],
      diff: "What Makes Us Different?",
      claim: "We're not here to fit in — we’re here to raise the standard.",
      own: "A Family Factory We Trust",
      ownText: "We work hand-in-hand with our family-run factory — ensuring unmatched quality, accountability, and transparency at every step.",
      cotton: "Fine Egyptian Cotton",
      cottonText: "Unmatched softness and durability — ethically sourced and proudly manufactured.",
      design: "Canadian Design",
      designText: "Tailored in Quebec for real life and refined taste. Zero shortcuts.",
      videoCaption: "See how your clothes are made — from our family-run factory to your doorstep.",
      sizing: "Tailored Fit for Real Bodies",
      sizingText: "Our sizing reflects real people — designed in Canada, adjusted for Canadian builds. Available in regular, slim, and oversized cuts.",
      why: "Why Maple + Cotton?",
      whyText: "Unlike brands using generic overseas factories, we craft in our own — built on family, quality, and ethical values. Every piece is a statement of accountability.",
      contact: "Contact Us",
      contactText: "We're here to help — whether it's about fit, fabric, or feedback.",
      name: "Your Name",
      email: "Your Email",
      message: "Your Message",
      send: "Send Message"
    },
    fr: {
      banner: "Parce que l'origine et la fabrication comptent",
      sub: "La plupart des marques ne peuvent pas retracer leurs produits. Nous créons les nôtres depuis la base.",
      tagline: "Éthique. Traçable. Conçu au Canada. Usine familiale.",
      bullets: [
        "Aucun intermédiaire. Aucun fournisseur mystérieux.",
        "Coton égyptien premium — sans mélanges ni tromperies.",
        "Prix transparents. Production éthique.",
        "Conçu au Canada. Adapté à la vie moderne."
      ],
      diff: "Ce Qui Nous Rend Différents",
      claim: "Nous ne cherchons pas à entrer dans le moule — nous élevons les standards.",
      own: "Nous Possédons l'Usine",
      ownText: "Contrôle complet du champ de coton à la couture finale. C’est ça, la transparence.",
      cotton: "Coton Égyptien de Qualité",
      cottonText: "Douceur et durabilité inégalées — sourcées éthiquement et fièrement fabriquées.",
      design: "Design Canadien",
      designText: "Conçu au Québec pour la vraie vie et un style raffiné. Aucun compromis.",
      videoCaption: "Découvrez comment vos vêtements sont fabriqués — de notre usine familiale à votre porte.",
      sizing: "Coupe Ajustée Pour Corps Réels",
      sizingText: "Notre taillage reflète les vraies personnes — conçu au Canada. Disponible en coupes régulière, ajustée et ample.",
      why: "Pourquoi Maple + Cotton?",
      whyText: "Contrairement aux marques produites dans des usines anonymes, nous produisons dans la nôtre — avec des valeurs familiales, éthiques et transparentes.",
      contact: "Contactez-Nous",
      contactText: "Nous sommes là pour vous aider — que ce soit sur la coupe, le tissu ou vos retours.",
      name: "Votre Nom",
      email: "Votre Email",
      message: "Votre Message",
      send: "Envoyer le Message"
    }
  };

  const copy = t[language];

  function handleOfflineFormSubmit(e) {
    e.preventDefault();
    const formData = new FormData(e.target);
    const values = Object.fromEntries(formData.entries());
    alert("Form Submitted:\n" + JSON.stringify(values, null, 2));
  }

  return (
    <div className="bg-white text-forest-green font-sans">
      <header className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
        <img src="logo.png" alt="Maple + Cotton Logo" className="h-14 sm:h-16" />
        <nav className="space-x-4 sm:space-x-6 text-sm font-medium text-center sm:text-right flex-1 flex flex-wrap justify-end items-center">
          <a href="#shop" className="hover:text-green-900">Shop</a>
          <a href="#story" className="hover:text-green-900">Our Story</a>
          <a href="#factory" className="hover:text-green-900">Our Factory</a>
          <a href="#sizing" className="hover:text-green-900">Sizing</a>
          <a href="#faq" className="hover:text-green-900">FAQ</a>
          <a href="#contact" className="hover:text-green-900">Contact</a>
          <button onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')} className="text-sm font-semibold text-forest-green ml-4">
            🇨🇦 {language === 'en' ? 'Canada FR' : 'Canada EN'}
          </button>
        </nav>
      </header>

      <motion.section id="story" className="text-center py-16 px-6 bg-[#f5f8f6]"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <h1 className="text-4xl md:text-5xl font-extrabold mb-6 uppercase tracking-wide">{copy.banner}</h1>
        <p className="max-w-2xl mx-auto text-lg text-gray-700">{copy.sub}</p>
        <p className="mt-4 text-lg font-semibold">{copy.tagline}</p>
      </motion.section>

      <motion.section id="shop" className="bg-white text-center py-16 px-6"
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.8 }}>
        <img src="logo.png" alt="Maple + Cotton Statement" className="mx-auto w-64 mb-6" />
        <ul className="grid gap-4 max-w-xl mx-auto text-left list-disc list-inside text-green-700">
          {copy.bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      </motion.section>

      <motion.section id="factory" className="bg-[#f1f5f3] py-16 px-6 text-center"
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 1 }}>
        <h2 className="text-3xl font-bold mb-4">{copy.diff}</h2>
        <p className="max-w-3xl mx-auto mb-8 text-gray-700">{copy.claim}</p>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div className="bg-white p-6 rounded shadow-sm">
            <h3 className="text-xl font-semibold mb-2">{copy.own}</h3>
            <p className="text-gray-600">{copy.ownText}</p>
          </div>
          <div className="bg-white p-6 rounded shadow-sm">
            <h3 className="text-xl font-semibold mb-2">{copy.cotton}</h3>
            <p className="text-gray-600">{copy.cottonText}</p>
          </div>
          <div className="bg-white p-6 rounded shadow-sm">
            <h3 className="text-xl font-semibold mb-2">{copy.design}</h3>
            <p className="text-gray-600">{copy.designText}</p>
          </div>
        </div>
        <div className="mt-12 max-w-3xl mx-auto">
          <iframe className="w-full aspect-video rounded shadow-lg" src="https://www.youtube.com/embed/2UzrsCK5HOs?si=e2VWDLwhLboYld51" title="Factory Tour - Maple + Cotton" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
          <p className="mt-4 text-sm text-gray-600">{copy.videoCaption}</p>
        </div>
      </motion.section>

      <motion.section id="sizing" className="bg-white py-16 px-6 text-center"
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.8 }}>
        <h2 className="text-3xl font-bold mb-4">{copy.sizing}</h2>
        <p className="text-gray-600 max-w-xl mx-auto mb-6">{copy.sizingText}</p>
        <img src="https://www.trueclassictees.com/cdn/shop/files/fittingroom_1_2000x.jpg" alt="Fit display" className="mx-auto rounded-lg shadow max-w-2xl" />
      </motion.section>

      <motion.section id="faq" className="bg-[#f5f8f6] py-16 px-6 text-center"
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.8 }}>
        <h2 className="text-3xl font-bold mb-6">{copy.why}</h2>
        <p className="text-lg max-w-3xl mx-auto text-gray-700">{copy.whyText}</p>
      </motion.section>

      <section id="contact" className="bg-white px-6 py-16 border-t">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-3xl font-semibold mb-4">{copy.contact}</h2>
          <p className="text-gray-700 mb-6">{copy.contactText}</p>
          <form className="space-y-4 text-left" onSubmit={handleOfflineFormSubmit}>
            <input name="name" type="text" placeholder={copy.name} className="w-full border px-4 py-2 rounded" required />
            <input name="email" type="email" placeholder={copy.email} className="w-full border px-4 py-2 rounded" required />
            <textarea name="message" placeholder={copy.message} rows="4" className="w-full border px-4 py-2 rounded" required></textarea>
            <button type="submit" className="bg-forest-green text-white px-6 py-2 rounded hover:bg-green-800">{copy.send}</button>
          </form>
        </div>
      </section>

      <footer className="bg-forest-green text-white text-center py-6">
        <p className="text-sm">© {new Date().getFullYear()} Maple + Cotton. All rights reserved.</p>
      </footer>
    </div>
  );
}
